import { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    LinearProgress,
    CircularProgress,
} from '@mui/material';
import { Timeline } from '@mui/icons-material';
import { GewichtApi } from '../../services/api';
import Notification from '../util/notifications/Notification';
import { buildNivoData } from './components/gewichtstatistik/gewichtStatistikUtils';
import GewichtsverlaufChart from './components/gewichtstatistik/GewichtsverlaufChart';

function GewichtStatistik({ nutzerId, compact = false }) {
    const [, setShowNotification] = useState(false);
    const [loading, setLoading] = useState(true);
    const [statistik, setStatistik] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (nutzerId) {
            fetchStatistik();
        }
    }, [nutzerId]);

    const fetchStatistik = async () => {
        try {
            setLoading(true);
            const response = await GewichtApi.getErweiterteStats(nutzerId);
            setStatistik(response.data);
            setMessage({ type: 'success', text: 'Statistik erfolgreich geladen' });
        } catch (err) {
            console.error('Fehler beim Laden der Statistik:', err);
            setMessage({ type: 'error', text: 'Fehler beim Laden der Statistik' });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Card
                sx={{
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                }}
            >
                <CircularProgress />
            </Card>
        );
    }

    if (!statistik) return null;

    const fortschritt = statistik.fortschritt || 0;
    const verlauf = statistik.gewichtsverlauf || [];
    const nivoData = buildNivoData(
        verlauf,
        statistik.start_gewicht,
        statistik.start_datum,
    );

    // Y-Achsen-Bereich mit etwas Puffer
    const alleGewichte = nivoData[0].data.map((d) => d.y);
    const minY = Math.min(...alleGewichte, Number(statistik.ziel_gewicht ?? Infinity)) - 2;
    const maxY = Math.max(...alleGewichte, Number(statistik.ziel_gewicht ?? -Infinity)) + 2;

    // Ziel-Linie als Nivo-Marker
    const zielMarker = statistik.ziel_gewicht != null
        ? [
            {
                axis: 'y',
                value: Number(statistik.ziel_gewicht),
                lineStyle: {
                    stroke: '#22c55e',
                    strokeWidth: 1.5,
                    strokeDasharray: '6 4',
                },
                legend: `Ziel ${Number(statistik.ziel_gewicht).toFixed(1)} kg`,
                legendPosition: 'bottom-right',
                legendOrientation: 'horizontal',
                textStyle: { fill: '#22c55e', fontSize: 11 },
            },
        ]
        : [];

    // Anzahl sichtbarer X-Ticks reduzieren auf mobil
    const tickCount = verlauf.length <= 6 ? verlauf.length : 4;

    return (
        <Box sx={{ mb: 3 }}>
            {message.type === 'error' && (
                <Notification
                    type={message.type}
                    message={message.text}
                    onClose={() => {
                        setShowNotification(true);
                        setMessage('');
                    }}
                />
            )}

            <Card
                sx={{
                    mb: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Header */}
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                        <Timeline sx={{ color: '#3b82f6', fontSize: 32 }} />
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ color: '#e0f2fe', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                        >
                            Gewichtsverlauf & Analyse
                        </Typography>
                    </Box>

                    {/* Fortschrittsbalken */}
                    <Box sx={{ mb: 4 }}>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="body2" sx={{ color: '#93c5fd' }}>
                                Fortschritt zum Ziel
                            </Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#e0f2fe' }}>
                                {fortschritt.toFixed(1)}%
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(fortschritt, 100)}
                            sx={{
                                height: 8,
                                borderRadius: '16px',
                                bgcolor: 'rgba(59, 130, 246, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: '16px',
                                    background:
                                        fortschritt >= 100
                                            ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)'
                                            : 'linear-gradient(90deg, #3b82f6 0%, #1e40af 100%)',
                                },
                            }}
                        />
                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            gap={{ xs: 0.5, sm: 0 }}
                            mt={1}
                        >
                            <Typography
                                variant="caption"
                                sx={{ color: '#64748b', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            >
                                Start: {statistik.start_gewicht?.toFixed(1)} kg
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: '#64748b', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            >
                                Aktuell: {statistik.aktuelles_gewicht?.toFixed(1)} kg
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: '#64748b', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            >
                                Ziel: {statistik.ziel_gewicht?.toFixed(1)} kg
                            </Typography>
                        </Box>
                    </Box>

                    {/* Nivo Line Chart */}
                    {verlauf.length > 0 && (
                        <GewichtsverlaufChart
                            nivoData={nivoData}
                            minY={minY}
                            maxY={maxY}
                            zielMarker={zielMarker}
                            tickCount={tickCount}
                            compact={compact}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default GewichtStatistik;
